import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import logger from '@widgets/logger';
import { useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import MicrositeFileUploader from '@features/MicrositeFileUploader';
import { ModalBase } from '@components/Modal';
import Overline from '@components/Overline';
import OutlinedIconButton from '@components/OutlinedIconButton';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import Button from '@components/Button';
import { useTranslation } from '@hooks';
import { getSurveyTextError } from '@helpers/surveyLanguages';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { getLocalizeLanguages } from '@constants/microsites';
import LocalizedFieldInput from './LocalizedFieldInput';

const REFERENCE_KEYS = ['surveyId', 'linkedMicrositeId', 'menuId', 'formId'];

const Modal = styled(ModalBase, { shouldForwardProp: prop => prop !== 'tall' })(({ tall }) => ({
    '& .modal-base-paper': {
        outline: 'none',
        width: '100%',
        height: tall ? 640 : 420,
        maxWidth: 652,
    },
    '& .modal-base-content': {
        padding: 16,
        overflow: 'auto',
    },
}));

const LanguageWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'withEntity' && prop !== 'withTitle',
})(({ withEntity, withTitle }) => ({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: `160px ${withTitle && withEntity ? '160px 1fr' : withTitle || withEntity ? '1fr' : ''}`,
    marginBottom: 8,
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
        gridGap: 0,
    },
}));

const AdditionalLanguageWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'withEntity' && prop !== 'withTitle',
})(({ withEntity, withTitle }) => ({
    display: 'grid',
    gridGap: 16,
    gridTemplateAreas: withEntity
        ? withTitle
            ? '"select input1 input2 button"'
            : '"select input2 button"'
        : withTitle
          ? '"select input1 button"'
          : '"select button"',
    gridTemplateColumns:
        withEntity && withTitle ? '160px 160px 1fr 40px' : withEntity || withTitle ? '160px 1fr 40px' : '160px 40px',
    marginBottom: 8,
    '@media (max-width: 600px)': {
        gridTemplateAreas: `"select button" ${withTitle ? '"input1 input1"' : ''} ${
            withEntity ? '"input2 input2"' : ''
        }`,
        gridTemplateColumns: '1fr 40px',
    },
}));

const LocalizeItemModal = ({
    modal,
    propName,
    entity,
    withChangeTitle = true,
    withChangeEntity,
    withUploadFile,
    onUploadFile,
    onClose,
    currentLocale,
    fields,
    languages,
    additionalRowsOptional = false,
}) => {
    const { setValue, getValues } = useFormContext();
    const [items, setItems] = useState(() => getValues(entity));
    const [isUploadingFile, setIsUploadingFile] = useState(false);

    const account = useSelector(state => state.account?.account);

    const { t } = useTranslation();

    const [inlineField, ...stackedFields] = fields || [];
    const hasFields = !!inlineField;
    const showTitleInput = withChangeTitle && !hasFields;

    const accountLanguages = getLocalizeLanguages(account.locale);
    const languageOptions = languages || accountLanguages;
    const findLanguageOption = value =>
        languageOptions.find(l => l.value === value) ||
        accountLanguages.find(l => l.value === value) || { label: value, value };
    const hasIndex = modal.index !== undefined && modal.index !== null;
    const allRows = hasIndex ? items?.[modal.index] : items;

    const isOffered = row => !languages || languages.some(lang => lang.value === row.language);
    const hasOfferedRow = Array.isArray(allRows) && allRows.some(isOffered);
    const rows = useMemo(() => (hasOfferedRow ? allRows.filter(isOffered) : allRows), [allRows, languages]);

    const updateRows = nextRows => {
        const merged = hasOfferedRow ? [...nextRows, ...allRows.filter(row => !isOffered(row))] : nextRows;
        if (!hasIndex) {
            setItems(merged);
            return;
        }
        const itemsCopy = cloneDeep(items);
        itemsCopy[modal.index] = merged;
        setItems(itemsCopy);
    };

    const defaultLanguage = useMemo(
        () => (rows?.find(l => l.language === currentLocale) || rows?.[0])?.language,
        [rows, currentLocale],
    );

    const fieldFor = (field, language) =>
        additionalRowsOptional && language !== defaultLanguage ? { ...field, optional: true } : field;

    const availableLanguages = useMemo(() => {
        return languageOptions?.filter(lang => !rows?.some(l => l.language === lang.value));
    }, [rows, languageOptions]);

    const questionOptions = useMemo(() => {
        if (entity !== 'questions' || !rows) return [];
        const firstItem = rows.find(l => l.language === currentLocale) || rows[0];
        return (firstItem?.options || []).filter(opt => !opt.hidden && !opt.isOther);
    }, [rows, entity, currentLocale]);

    const onChangeLink = (e, language) => {
        updateRows(rows.map(l => (l.language === language ? { ...l, [e.target.name]: e.target.value } : l)));
    };

    const onChangeOptionLabel = (optionId, value, language) => {
        updateRows(
            rows.map(l => {
                if (l.language !== language) return l;
                const options = (l.options || []).map(opt => (opt.id === optionId ? { ...opt, label: value } : opt));
                return { ...l, options };
            }),
        );
    };

    const onChangeFile = async (e, language) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            setIsUploadingFile(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await onUploadFile(data);
            updateRows(rows.map(l => (l.language === language ? { ...l, fileUrl: response.publicUrl } : l)));
        } catch (_) {
            logger.error("Couldn't upload file microsite");
        } finally {
            setIsUploadingFile(false);
            e.target.value = '';
        }
    };

    const referenceKey = REFERENCE_KEYS.find(key => rows?.[0]?.[key] != null);

    const onAddLanguage = () => {
        const nextRows = cloneDeep(rows);
        if (hasFields) {
            nextRows.push({
                language: availableLanguages[0].value || '',
                ...Object.fromEntries(fields.map(field => [field.name, ''])),
            });
        } else if (referenceKey) {
            nextRows.push({
                [referenceKey]: rows?.[0]?.[referenceKey],
                title: t('common.title'),
                link: '',
                language: availableLanguages[0].value || '',
            });
        } else if (modal?.ctx?.isFile) {
            nextRows.push({
                fileUrl: '',
                title: rows?.[0]?.title,
                link: '',
                language: availableLanguages[0].value || '',
            });
        } else {
            const newItem = {
                title: rows?.[0]?.title,
                [propName]: '',
                language: availableLanguages[0].value || '',
            };
            if (entity === 'questions') {
                const sourceItem = rows.find(l => l.language === currentLocale) || rows[0];
                const allOptions = sourceItem?.options || [];
                if (allOptions.length > 0) {
                    newItem.options = allOptions.map(opt => ({
                        ...opt,
                        label: opt.hidden || opt.isOther ? opt.label : '',
                    }));
                }
                newItem.type = sourceItem?.type;
            }
            nextRows.push(newItem);
        }
        updateRows(nextRows);
    };

    const onDeleteAdditional = language => {
        updateRows(rows.filter(l => l.language !== language));
    };

    const onSave = () => {
        setValue(entity, items, { shouldDirty: true, shouldValidate: true });
        onClose();
    };

    const isValid = useMemo(
        () =>
            rows?.every(item => {
                if (hasFields) {
                    return fields.every(field => !getSurveyTextError(item[field.name], fieldFor(field, item.language)));
                }
                const titleValid = !withChangeTitle || !!item.title;
                const baseValid =
                    titleValid && (!!item[propName] || !!item.fileUrl || REFERENCE_KEYS.some(key => !!item[key]));
                if (!baseValid) return false;
                // If this question has options, all visible labels must be non-empty
                if (entity === 'questions' && item.options?.length > 0) {
                    return item.options.filter(o => !o.hidden && !o.isOther).every(o => !!o.label?.trim());
                }
                return true;
            }),
        [rows],
    );

    if (!rows?.length) return null;

    const firstItem = rows.find(l => l.language === currentLocale) || rows[0];

    const localizedItems = rows.filter(l => l.language !== firstItem.language);

    const renderFieldInput = (field, item, language, sx) => (
        <LocalizedFieldInput
            key={field.name}
            field={fieldFor(field, language)}
            value={item?.[field.name]}
            onChange={e => onChangeLink(e, language)}
            sx={sx}
        />
    );

    const renderStackedFields = (item, language) => stackedFields.map(field => renderFieldInput(field, item, language));

    const renderOptionInputs = (item, language) => {
        if (entity !== 'questions' || questionOptions.length === 0) return null;
        const itemOptions = item.options || [];
        return (
            <div style={{ marginTop: 4, marginBottom: 8 }}>
                {questionOptions.map((opt, idx) => {
                    const localizedOpt = itemOptions.find(o => o.id === opt.id);
                    return (
                        <div key={opt.id} style={{ marginBottom: 4 }}>
                            <Input
                                inputProps={{
                                    value: localizedOpt?.label || '',
                                    onChange: e => onChangeOptionLabel(opt.id, e.target.value, language),
                                }}
                                label={`${t('FeedbackSurveys.optionLabel')} ${idx + 1}`}
                                fullWidth
                                required
                                error={!localizedOpt?.label?.trim()}
                                helperText={!localizedOpt?.label?.trim() ? t('validation.required') : ''}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <Modal
            fullScreen
            tall={hasFields}
            isOpen={modal.open}
            handleClose={onClose}
            title={`${t('common.localization')} - ${firstItem?.title || ''}`}
            onPrimaryAction={onSave}
            primaryActionDisabled={!isValid}
            primaryActionText={t('buttons.save')}
        >
            <div>
                <Overline variant="caption" sx={{ marginBottom: '16px' }}>
                    {t('LocalizeItemModal.defaultLanguages')}
                </Overline>
                <LanguageWrapper withTitle={showTitleInput || !!inlineField} withEntity={withChangeEntity}>
                    <Select
                        value={firstItem?.language}
                        name="language"
                        onChange={e => onChangeLink(e, firstItem?.language)}
                        label={t('common.language')}
                        disabled
                        fullWidth
                        options={[findLanguageOption(firstItem?.language)]}
                    />
                    {inlineField ? renderFieldInput(inlineField, firstItem, firstItem?.language) : null}
                    {showTitleInput ? (
                        <Input
                            inputProps={{
                                name: 'title',
                                value: firstItem?.title,
                                onChange: e => onChangeLink(e, firstItem?.language),
                            }}
                            error={!firstItem?.title}
                            helperText={!firstItem?.title ? t('validation.required') : ''}
                            required
                            label={t('common.title')}
                            fullWidth
                        />
                    ) : null}
                    {withChangeEntity && !withUploadFile ? (
                        <Input
                            inputProps={{
                                name: propName,
                                value: firstItem?.[propName],
                                onChange: e => onChangeLink(e, firstItem?.language),
                            }}
                            label={t(`common.${propName}`)}
                            required
                            error={!firstItem?.[propName]}
                            helperText={!firstItem?.[propName] ? t('validation.required') : ''}
                            fullWidth
                        />
                    ) : null}
                    {withUploadFile ? (
                        <MicrositeFileUploader
                            item={firstItem}
                            disabled={isUploadingFile}
                            onChange={e => onChangeFile(e, firstItem?.language)}
                        />
                    ) : null}
                </LanguageWrapper>
                {renderStackedFields(firstItem, firstItem?.language)}
                {renderOptionInputs(firstItem, firstItem?.language)}
                <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}
                >
                    <Overline variant="caption">{t('LocalizeItemModal.additionalLanguages')}</Overline>
                    <Button variant="outlined" onClick={onAddLanguage} disabled={!availableLanguages.length}>
                        {t('buttons.addLanguage')}
                    </Button>
                </div>
                {localizedItems.map((item, index) => {
                    const langOptions = [findLanguageOption(item.language), ...availableLanguages];
                    if (hasFields) {
                        return (
                            <div key={index}>
                                <AdditionalLanguageWrapper withTitle>
                                    <Select
                                        value={item?.language}
                                        name="language"
                                        onChange={e => onChangeLink(e, item?.language)}
                                        label={t('common.language')}
                                        fullWidth
                                        options={langOptions}
                                        style={{ gridArea: 'select' }}
                                    />
                                    {renderFieldInput(inlineField, item, item?.language, { gridArea: 'input1' })}
                                    <OutlinedIconButton
                                        sx={{ gridArea: 'button' }}
                                        onClick={() => onDeleteAdditional(item?.language)}
                                    >
                                        <DeleteIcon />
                                    </OutlinedIconButton>
                                </AdditionalLanguageWrapper>
                                {renderStackedFields(item, item?.language)}
                            </div>
                        );
                    }
                    return (
                        <div key={index}>
                            <AdditionalLanguageWrapper withTitle={withChangeTitle} withEntity={withChangeEntity}>
                                <Select
                                    value={item?.language}
                                    name="language"
                                    onChange={e => onChangeLink(e, item?.language)}
                                    label={t('common.language')}
                                    fullWidth
                                    options={langOptions}
                                    style={{ gridArea: 'select' }}
                                />
                                {withChangeTitle ? (
                                    <Input
                                        inputProps={{
                                            name: 'title',
                                            value: item?.title,
                                            onChange: e => onChangeLink(e, item?.language),
                                        }}
                                        required
                                        error={!item?.title}
                                        helperText={!item?.title ? t('validation.required') : ''}
                                        sx={{ gridArea: 'input1' }}
                                        label={t('common.title')}
                                        fullWidth
                                    />
                                ) : null}
                                {withChangeEntity && !withUploadFile ? (
                                    <Input
                                        inputProps={{
                                            name: propName,
                                            value: item?.[propName],
                                            onChange: e => onChangeLink(e, item?.language),
                                        }}
                                        required
                                        error={!item?.[propName]}
                                        helperText={!item?.[propName] ? t('validation.required') : ''}
                                        sx={{ gridArea: 'input2' }}
                                        label={t(`common.${propName}`)}
                                        fullWidth
                                    />
                                ) : null}
                                {withUploadFile ? (
                                    <MicrositeFileUploader
                                        item={item}
                                        disabled={isUploadingFile}
                                        onChange={e => onChangeFile(e, item?.language)}
                                        styles={{ gridArea: 'input2' }}
                                    />
                                ) : null}
                                <OutlinedIconButton
                                    sx={{ gridArea: 'button' }}
                                    onClick={() => onDeleteAdditional(item?.language)}
                                >
                                    <DeleteIcon />
                                </OutlinedIconButton>
                            </AdditionalLanguageWrapper>
                            {renderOptionInputs(item, item?.language)}
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

export default LocalizeItemModal;
